import { gql } from "@apollo/client";
import { alpha, ButtonBase, Collapse, Paper } from "@mui/material";
import overviewHeaderBgJpg from "assets/overview-header-bg.jpg";
import Spacer from "atoms/Spacer";
import VideoThumbnail from "atoms/VideoThumbnail";
import { COLOR_BLACK, COLOR_PRIMARY, COLOR_WHITE, getStatColor, TEAM_COLORS } from "helpers/colors";
import { VIDEO_PLAYER_RATIO } from "helpers/constants";
import { ONSEN_APP } from "helpers/environment";
import { FONT_FAMILY_LEAGUE_GOTHIC } from "helpers/FONT";
import { gaEvent } from "helpers/gtag";
import { SPACING, SPACING_DENSE, SPACING_PADDING } from "helpers/spacings";
import { Download } from "mdi-material-ui";
import Subnav, { SubnavDropdown, SubnavToggleLabel } from "molecules/Subnav";
import React, { useState } from "react";
import { useComponentSize } from "react-use-size";

import GamePlayerSelector, { GamePlayerSelectorFragment } from "./GamePlayerSelector";

// These sizing are just locally for the header to contain layout, do not reuse.
const CONTENT_WIDTH = 1080;
const SIDE_CARD_WIDTH = 300;

export const GameOverviewPageHeaderFragment = gql`
  fragment GameOverviewPageHeaderFragment on Game {
    videoDownloadUrl
    videoProcessing
    videoArchived
    thumbnailUrl
    videoHlsUrl
    homeScore
    home {
      id
      name
      colour
    }
    awayScore
    away {
      id
      name
      colour
    }
    thumbnailUrl
    statsVisible
    ...GamePlayerSelectorFragment
  }
  ${GamePlayerSelectorFragment}
`;

export const GameOverviewPageHeaderGamePlayerFragment = gql`
  fragment GameOverviewPageHeaderGamePlayerFragment on GamePlayer {
    id
    playerNumber
    team {
      id
      name
      colour
    }
    person {
      id
      fullNameWithAnonymisation
    }
    overviewStats {
      name
      value
    }
  }
`;

export default function GameOverviewPageHeader({ game, gamePlayer, gamePlayerIdSet, tab, tabSet }) {
  const color = TEAM_COLORS[gamePlayer?.team.colour || game?.home.colour] || COLOR_PRIMARY;
  const [downloadHovering, downloadHoveringSet] = useState(false);
  const componentSize = useComponentSize();

  return (
    <>
      <div
        ref={componentSize.ref}
        style={{
          background: [
            //
            `linear-gradient(to right, ${alpha(color, 0.94)}, ${alpha(color, 0.94)})`,
            `url(${overviewHeaderBgJpg})`,
          ].join(", "),
          color: COLOR_WHITE,
          fontWeight: "bold",
        }}
      >
        <div style={{ maxWidth: CONTENT_WIDTH, margin: "0 auto 15px auto" }}>
          <div
            style={{
              padding: SPACING_PADDING,
              gap: SPACING,
              display: "flex",
              alignItems: "center",
              justifyContent: "stretch",
              flexFlow: "row nowrap",
              fontSize: "min(3vw, 30px)",
              fontFamily: FONT_FAMILY_LEAGUE_GOTHIC,
              textTransform: "uppercase",
            }}
          >
            {game?.statsVisible && (
              <>
                <div style={{ flex: "1 1 80em", fontSize: "1.5em" }}>{game?.home.name}</div>
                <div style={{ fontSize: "2em" }}>{game?.homeScore}</div>
                <div style={{ fontSize: 10, opacity: 0.5 }}>VS</div>
                <div style={{ fontSize: "2em" }}>{game?.awayScore}</div>
                <div
                  style={{
                    flex: "1 1 80em",
                    textAlign: "right",
                    fontSize: "1.5em",
                  }}
                >
                  {game?.away.name}
                </div>
              </>
            )}
          </div>

          <Subnav transparent>
            <SubnavDropdown
              text={gamePlayer && `${gamePlayer.person.fullNameWithAnonymisation} #${gamePlayer.playerNumber}`}
              disabled={!gamePlayer}
            >
              <GamePlayerSelector game={game} gamePlayerId={gamePlayer?.id} gamePlayerIdSet={gamePlayerIdSet} />
            </SubnavDropdown>
            <SubnavToggleLabel text="Overview" checked={!tab} onClick={() => tabSet(null)} />
            {game?.statsVisible && (
              <>
                <SubnavToggleLabel text="Highlight reels" checked={tab === "reels"} onClick={() => tabSet("reels")} />
                <SubnavToggleLabel
                  text="Highlights"
                  checked={tab === "highlights"}
                  onClick={() => tabSet("highlights")}
                />
                <SubnavToggleLabel
                  text="Shot chart"
                  checked={tab === "shotchart"}
                  onClick={() => tabSet("shotchart")}
                />
                <SubnavToggleLabel text="Box score" checked={tab === "boxscore"} onClick={() => tabSet("boxscore")} />
                <SubnavToggleLabel
                  text="Point chart"
                  checked={tab === "pointchart"}
                  onClick={() => tabSet("pointchart")}
                />
              </>
            )}
            <Spacer />
            {!ONSEN_APP && (
              <div style={{ height: 32 }}>
                <ButtonBase
                  onMouseEnter={() => downloadHoveringSet(true)}
                  onMouseLeave={() => downloadHoveringSet(false)}
                  {...(game?.videoDownloadUrl
                    ? {
                        href: game.videoDownloadUrl,
                        target: "_blank",
                        rel: "noopener noreferrer",
                      }
                    : {})}
                  disabled={!game?.videoDownloadUrl}
                  onClick={() => {
                    if (game?.videoDownloadUrl) {
                      gaEvent("download", { type: "event" });
                    }
                  }}
                  title={"Download Game Video"}
                  style={{
                    border: `1px solid ${COLOR_WHITE}`,
                    borderRadius: 999,
                    padding: "9px 16px",
                    backgroundColor: downloadHovering ? COLOR_WHITE : "transparent",
                    color: downloadHovering ? COLOR_BLACK : COLOR_WHITE,
                    fontSize: 12,
                    display: "flex",
                    alignItems: "center",
                    opacity: game?.videoDownloadUrl ? 1 : 0.5,
                    transition: "background-color 0.2s ease, color 0.2s ease",
                  }}
                >
                  <Download
                    sx={{ fontSize: 12, marginRight: 0.5, color: downloadHovering ? COLOR_BLACK : COLOR_WHITE }}
                  />
                  <span>Download Game Video</span>
                </ButtonBase>
              </div>
            )}
          </Subnav>
          <Collapse in={!tab}>
            <div
              style={{
                display: "flex",
                flexFlow: "row nowrap",
                justifyContent: "center",
                alignItems: "stretch",
                padding: SPACING_PADDING,
                gap: SPACING,
              }}
            >
              <ButtonBase
                disabled={!game?.videoHlsUrl}
                href={`/game/${game?.id}/video`}
                style={{
                  flex: "1 1 auto",
                  aspectRatio: VIDEO_PLAYER_RATIO,
                  position: "relative",
                }}
              >
                <VideoThumbnail
                  component={Paper}
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                  }}
                  processing={game?.videoProcessing}
                  thumbnailUrl={game?.thumbnailUrl}
                  videoArchived={game?.videoArchived}
                />
              </ButtonBase>

              {game?.statsVisible && componentSize.width > 780 && (
                <Paper
                  style={{
                    //
                    width: SIDE_CARD_WIDTH,
                    flex: "0 0 auto",
                    display: "flex",
                    flexFlow: "column nowrap",
                    justifyContent: "stretch",
                  }}
                >
                  {gamePlayer?.overviewStats.map((stat) => (
                    <div
                      key={stat.name}
                      style={{
                        flex: "1 1 auto",
                        padding: SPACING_DENSE,
                        borderBottom: `1px solid ${alpha(COLOR_BLACK, 0.1)}`,
                        display: "flex",
                        flexFlow: "row nowrap",
                        justifyContent: "stretch",
                        alignItems: "center",
                        gap: SPACING_DENSE,
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: getStatColor(stat.name),
                          color: COLOR_WHITE,
                          paddingLeft: SPACING_DENSE,
                          paddingRight: SPACING_DENSE,
                          borderRadius: SPACING_DENSE,
                          width: "2em",
                          height: "2em",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {stat.name[0]}
                      </div>
                      <div style={{ flex: "1 1 auto" }}>{stat.name}</div>
                      {stat.value ? (
                        <div
                          style={{
                            color: getStatColor(stat.name),
                            fontWeight: "bold",
                          }}
                        >
                          {stat.value}
                        </div>
                      ) : (
                        <ButtonBase
                          disabled={!game?.videoHlsUrl}
                          href={`/game/${game?.id}/video`}
                          style={{
                            padding: "4px 8px",
                            fontSize: 10,
                            border: "1px solid",
                            opacity: 0.5,
                            borderRadius: 8,
                          }}
                        >
                          Tag {stat.name}
                        </ButtonBase>
                      )}
                    </div>
                  ))}
                </Paper>
              )}
            </div>
          </Collapse>
        </div>
      </div>
    </>
  );
}
